<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
  IconApps,
  IconCheckCircle,
  IconDashboard,
  IconDown,
  IconFile,
  IconSafe,
  IconSearch,
  IconSend,
  IconSettings,
  IconStorage,
  IconNotification,
} from '@arco-design/web-vue/es/icon'

import { useAuthStore } from '@/modules/auth/store'
import { reminderBadgeRevision, UNREAD_BADGE_POLL_MS } from '@/modules/reporting/reminderBadge'
import { inAppUnreadAlertsPath } from '@/modules/reporting/reportingModel'
import { api } from '@/shared/api/http'
import { customerMoneyPermissions, customerRefundPermissions, customerStatementPermissions } from '@/app/access'
import { entities, reports } from '@/modules/catalog'
import { showEntityInNav, showReportInSimpleNav } from '@/modules/simpleMode'
import { resolvePageTitle } from '@/shared/page/pageTitles'
import { searchMenu } from './menuSearch'
import { canShowApprovalsNav, canShowReportItem, canShowReportsNav } from './sidebarNav'
import LanguageSwitch from '@/shared/components/LanguageSwitch.vue'
import { feeReadPermissions } from '@/modules/procurement/landedCostForm'
import { returnReadPermissions } from '@/modules/procurement/supplierReturnForm'
import { freightReadPermissions } from '@/modules/sales/freightForm'
import { aftersaleReadPermissions } from '@/modules/sales/aftersalesForm'
import { returnReadPermissions as customerReturnReadPermissions } from '@/modules/sales/aftersalesReturnForm'
import { replacementReadPermissions } from '@/modules/sales/aftersalesReplacementForm'
import { invoiceReadPermissions } from '@/modules/finance/invoiceForm'
import { paymentReadPermissions } from '@/modules/finance/paymentForm'
import { creditReadPermissions } from '@/modules/finance/supplierCredit'
import { refundReadPermissions as supplierRefundReadPermissions } from '@/modules/finance/supplierRefund'
import { supplierActivityPermissions } from '@/modules/finance/supplierActivity'

const auth = useAuthStore()
const router = useRouter()
const route = useRoute()
const { locale, t } = useI18n()
const groups = computed(() => {
  const result: Record<string, typeof entities[string][]> = {}
  for (const item of Object.values(entities)) {
    if (item.navigation === false || !showEntityInNav(item.key) || !auth.can(item.permission)) continue
    ;(result[item.group] ??= []).push(item)
  }
  if (canApprovalFlows.value) result['系统'] ??= []
  if (canRolePermissions.value) result['系统'] ??= []
  if (canUserAdmin.value) result['系统'] ??= []
  if (canDocumentNumbering.value) result['系统'] ??= []
  return result
})
// 菜单分组顺序：业务分组 → 报表分析 → 系统（系统固定在最下面）。
const orderedGroups = computed(() => {
  const entries = Object.entries(groups.value).filter(([group]) => group !== '系统')
  const result: [string, typeof entities[string][]][] = [...entries]
  if (showReportsGroup.value) result.push(['reports', []])
  if (groups.value['系统']) result.push(['系统', groups.value['系统']])
  return result
})
const canApprovalFlows = computed(() => auth.can('workflow.view_approvalflow') && auth.can('workflow.change_approvalflow'))
const canRolePermissions = computed(() => auth.can('auth.view_group') || auth.can('auth.change_group'))
const canUserAdmin = computed(() => auth.can('accounts.view_user') || auth.can('accounts.change_user') || auth.can('accounts.add_user'))
const canDocumentNumbering = computed(() => Boolean(auth.user?.is_staff))
const canReportingRules = computed(() => auth.can('reporting.view_reportingrule'))
const canReminders = computed(() => auth.can('reporting.view_reportingalert'))
const unreadCount = ref(0)
let unreadEpoch = 0

async function refreshUnread() {
  if (!canReminders.value) {
    unreadCount.value = 0
    return
  }
  const stamp = JSON.stringify(auth.user)
  const current = ++unreadEpoch
  try {
    const result = await api<{ count?: number }>(inAppUnreadAlertsPath(), { cache: 'no-store' })
    if (stamp !== JSON.stringify(auth.user) || current !== unreadEpoch) return
    const count = result.count
    unreadCount.value = typeof count === 'number' && Number.isSafeInteger(count) && count > 0 ? count : 0
  } catch {
    if (stamp === JSON.stringify(auth.user) && current === unreadEpoch) unreadCount.value = 0
  }
}

const bellLabel = computed(() => {
  const name = locale.value === 'en' ? 'Notifications' : '站内提醒'
  if (unreadCount.value <= 0) return name
  const count = unreadCount.value > 99 ? '99+' : String(unreadCount.value)
  return locale.value === 'en' ? `${name}, ${count} unread` : `${name}，${count} 条未读`
})
const canApprovals = computed(() => canShowApprovalsNav(auth.can))
const showReportsGroup = computed(() => canShowReportsNav(auth.can, visibleReports.value.length))

// 根据当前路由自动展开所属分组，保证从链接/刷新进入时菜单高亮可见。
const openKeys = ref<string[]>([])
function groupForPath(path: string): string | undefined {
  if (path.startsWith('/reports/') || path === '/settings/inventory-analysis' || path === '/notifications') return 'reports'
  if (path === '/settings/approval-flows' || path === '/settings/roles' || path === '/settings/users'
    || path === '/settings/document-numbers') return '系统'
  if (path.startsWith('/records/')) {
    const cfg = entities[path.slice('/records/'.length).split(/[/?#]/)[0] ?? '']
    return cfg?.group
  }
  if (path.startsWith('/sales/')) return '销售'
  if (path.startsWith('/finance/')) return '财务'
  if (path.startsWith('/procurement/')) return '采购'
  return undefined
}
watch(() => route.path, (path) => {
  const key = groupForPath(path)
  if (key && !openKeys.value.includes(key)) openKeys.value = [...openKeys.value, key]
  void refreshUnread()
}, { immediate: true })
watch(canReminders, () => { void refreshUnread() })
// 标记已读 / 重新评估不改变路径，路由监听不会再请求未读数。
watch(reminderBadgeRevision, () => { void refreshUnread() })
const visibleReports = computed(() => reports.filter((item) => showReportInSimpleNav(item.key)
  && canShowReportItem(item, auth.can)))
const groupKeys: Record<string, string> = {
  基础档案: 'masterData', 采购: 'procurement', 销售: 'sales', 库存: 'inventory', 财务: 'finance', 系统: 'system',
}
const groupIcons: Record<string, typeof IconApps> = {
  加工: IconStorage,
  基础档案: IconApps, 采购: IconFile, 销售: IconSend, 库存: IconStorage, 财务: IconSafe, 系统: IconSettings,
}

const pageTitle = computed(() => resolvePageTitle(route, locale.value === 'en', t))

function groupLabel(group: string) {
  if (group === '加工') return locale.value === 'en' ? 'Processing' : '加工'
  const key = groupKeys[group]
  return key ? t(`nav.groups.${key}`) : group
}

async function signOut() {
  await auth.logout()
  await router.replace('/login')
}

function onUserSelect(value: unknown) {
  if (value === 'change-password') void router.push('/change-password')
  else if (value === 'logout') void signOut()
}

// Arco 的下拉菜单不响应 Esc（焦点不在触发器上），这里显式接管，
// 否则用户按 Esc 后菜单停留在屏幕上、再点名字反而把它关掉（状态错位）。
const userMenuOpen = ref(false)

function onUserMenuVisible(visible: boolean) {
  userMenuOpen.value = visible
}

function onGlobalKeydown(event: KeyboardEvent) {
  if (event.key === 'Escape') userMenuOpen.value = false
}

// 内容区是独立滚动容器（不是 window），切换页面时手动回到顶部。
const contentRef = ref<{ $el?: HTMLElement } | null>(null)
watch(() => route.path, () => { contentRef.value?.$el?.scrollTo?.({ top: 0 }) })

// 菜单搜索：从已渲染（已按权限过滤）的菜单项取名称；选中后复用菜单项自身的跳转，
// 路由变化时 groupForPath 会自动展开所属分组。
const siderRef = ref<{ $el?: HTMLElement } | null>(null)
const menuQuery = ref('')
const menuActive = ref(0)
interface MenuEntry { label: string, group?: string, el: HTMLElement }
const menuIndex = ref<MenuEntry[]>([])
function rebuildMenuIndex() {
  const root = siderRef.value?.$el
  if (!root) return
  menuIndex.value = Array.from(root.querySelectorAll<HTMLElement>('.arco-menu-item')).map((el) => ({
    el,
    label: (el.textContent ?? '').trim(),
    group: el.closest('.arco-menu-inline')?.querySelector('.arco-menu-inline-header')?.textContent?.trim() || undefined,
  })).filter((entry) => entry.label)
}
const menuResults = computed(() => searchMenu(menuIndex.value, menuQuery.value))
watch(menuQuery, (value) => {
  menuActive.value = 0
  if (value) rebuildMenuIndex()
})
function openMenuResult(entry?: MenuEntry) {
  if (!entry) return
  entry.el.click()
  menuQuery.value = ''
}
function onMenuSearchKey(event: KeyboardEvent) {
  const count = menuResults.value.length
  if (event.key === 'ArrowDown' && count) {
    event.preventDefault()
    menuActive.value = (menuActive.value + 1) % count
  } else if (event.key === 'ArrowUp' && count) {
    event.preventDefault()
    menuActive.value = (menuActive.value - 1 + count) % count
  } else if (event.key === 'Enter') {
    openMenuResult(menuResults.value[menuActive.value])
  } else if (event.key === 'Escape') {
    menuQuery.value = ''
  }
}

let unreadTimer = 0
function stopUnreadTimer() {
  if (!unreadTimer) return
  window.clearInterval(unreadTimer)
  unreadTimer = 0
}
function startUnreadTimer() {
  stopUnreadTimer()
  unreadTimer = window.setInterval(() => { void refreshUnread() }, UNREAD_BADGE_POLL_MS)
}
function onVisible() {
  if (document.visibilityState !== 'visible') {
    stopUnreadTimer()
    return
  }
  void refreshUnread()
  startUnreadTimer()
}
function openNotifications() {
  // 已在收件箱时再点铃铛不会改 path，路由监听不会补拉。
  if (route.path === '/notifications') void refreshUnread()
  else void router.push('/notifications')
}

onMounted(() => {
  window.addEventListener('keydown', onGlobalKeydown)
  document.addEventListener('visibilitychange', onVisible)
  if (document.visibilityState === 'visible') startUnreadTimer()
})
onBeforeUnmount(() => {
  stopUnreadTimer()
  unreadEpoch += 1
  window.removeEventListener('keydown', onGlobalKeydown)
  document.removeEventListener('visibilitychange', onVisible)
})
</script>

<template>
  <a-layout class="app-shell">
    <a-layout-sider ref="siderRef" :width="224" breakpoint="lg" collapsible>
      <div class="app-logo">CW <span>{{ t('appName') }}</span></div>
      <div class="menu-search" data-testid="menu-search">
        <a-input v-model="menuQuery" allow-clear size="small" data-testid="menu-search-input"
          :placeholder="locale === 'en' ? 'Search menu' : '搜索菜单'" @keydown="onMenuSearchKey">
          <template #prefix><IconSearch /></template>
        </a-input>
        <ul v-if="menuQuery.trim()" class="menu-search-results" data-testid="menu-search-results">
          <li v-for="(entry, index) in menuResults" :key="`${entry.group}-${entry.label}`"
            :class="{ active: index === menuActive }" @mousedown.prevent="openMenuResult(entry)" @mouseenter="menuActive = index">
            <span>{{ entry.label }}</span><small v-if="entry.group">{{ entry.group }}</small>
          </li>
          <li v-if="!menuResults.length" class="empty">{{ locale === 'en' ? 'No match' : '没有匹配的菜单' }}</li>
        </ul>
      </div>
      <a-menu :selected-keys="[router.currentRoute.value.fullPath]" v-model:open-keys="openKeys">
        <a-menu-item v-if="canApprovals" key="/approvals" data-testid="nav-approvals"
          @click="router.push('/approvals')"><IconCheckCircle />
          <span>{{ t('nav.approvals') }}</span></a-menu-item>
        <template v-for="[group, items] in orderedGroups" :key="group">
          <a-sub-menu v-if="group === 'reports'" key="reports" data-testid="nav-reports">
            <template #title><IconDashboard /><span>{{ t('nav.reports') }}</span></template>
            <a-menu-item v-for="item in visibleReports" :key="`/reports/${item.key}`" @click="router.push(`/reports/${item.key}`)">
              {{ locale === 'en' ? item.titleEn : item.title }}
            </a-menu-item>
            <a-menu-item v-if="canReportingRules" key="/settings/inventory-analysis" @click="router.push('/settings/inventory-analysis')">
              {{ locale === 'en' ? 'Inventory analysis and alert rules' : '库存分析与预警规则' }}</a-menu-item>
            <a-menu-item v-if="canReminders" key="/notifications" @click="router.push('/notifications')">
              {{ locale === 'en' ? 'In-app reminders' : '站内提醒' }}</a-menu-item>
          </a-sub-menu>
          <a-sub-menu v-else :key="group">
            <template #title><component :is="groupIcons[group]" /><span>{{ groupLabel(group) }}</span></template>
            <a-menu-item v-for="item in items" :key="`/records/${item.key}`" @click="router.push(`/records/${item.key}`)">
              {{ locale === 'en' ? item.titleEn : item.title }}
            </a-menu-item>
            <a-menu-item v-if="group === '销售' && freightReadPermissions.every(auth.can)" key="/sales/freights" @click="router.push('/sales/freights')">
              {{ locale === 'en' ? 'Sales freight' : '销售运输费用' }}
            </a-menu-item>
            <a-menu-item v-if="group === '财务' && customerMoneyPermissions.every(auth.can)" key="/finance/customer-money" @click="router.push('/finance/customer-money')">
              {{ locale === 'en' ? 'Customer receipts and credits' : '客户收款与贷项' }}
            </a-menu-item>
            <a-menu-item v-if="group === '财务' && customerRefundPermissions.every(auth.can)" key="/finance/customer-refunds" @click="router.push('/finance/customer-refunds')">
              {{ locale === 'en' ? 'Customer refunds and corrections' : '客户退款与反向' }}
            </a-menu-item>
            <a-menu-item v-if="group === '财务' && customerStatementPermissions.every(auth.can)" key="/finance/customer-statement" @click="router.push('/finance/customer-statement')">
              {{ locale === 'en' ? 'Customer period statement' : '客户期间对账' }}
            </a-menu-item>
            <a-menu-item v-if="group === '财务' && invoiceReadPermissions.every(auth.can)" key="/finance/supplier-invoices" @click="router.push('/finance/supplier-invoices')">
              {{ locale === 'en' ? 'Supplier invoices' : '供应商发票' }}
            </a-menu-item>
            <a-menu-item v-if="group === '财务' && paymentReadPermissions.every(auth.can)" key="/finance/payment-instructions" @click="router.push('/finance/payment-instructions')">
              {{ locale === 'en' ? 'Payment requests' : '付款申请' }}
            </a-menu-item>
            <a-menu-item v-if="group === '财务' && creditReadPermissions.every(auth.can)" key="/finance/supplier-credits" @click="router.push('/finance/supplier-credits')">
              {{ locale === 'en' ? 'Supplier credit use' : '供应商贷项' }}
            </a-menu-item>
            <a-menu-item v-if="group === '财务' && supplierRefundReadPermissions.every(auth.can)" key="/finance/supplier-refunds" @click="router.push('/finance/supplier-refunds')">
              {{ locale === 'en' ? 'Supplier refunds' : '供应商退款' }}
            </a-menu-item>
            <a-menu-item v-if="group === '财务' && supplierActivityPermissions.every(auth.can)" key="/finance/supplier-activity" @click="router.push('/finance/supplier-activity')">
              {{ locale === 'en' ? 'Supplier statement' : '供应商往来' }}
            </a-menu-item>
            <a-menu-item v-if="group === '销售' && aftersaleReadPermissions.every(auth.can)" key="/sales/after-sales" @click="router.push('/sales/after-sales')">
              {{ locale === 'en' ? 'Customer claims' : '客户售后' }}
            </a-menu-item>
            <a-menu-item v-if="group === '销售' && customerReturnReadPermissions.every(auth.can)" key="/sales/after-sale-returns" @click="router.push('/sales/after-sale-returns')">
              {{ locale === 'en' ? 'Customer actual returns' : '客户实物退回' }}
            </a-menu-item>
            <a-menu-item v-if="group === '销售' && replacementReadPermissions.every(auth.can)" key="/sales/after-sale-replacements" @click="router.push('/sales/after-sale-replacements')">
              {{ locale === 'en' ? 'Linked replacements' : '关联补寄' }}
            </a-menu-item>
            <a-menu-item v-if="group === '采购' && feeReadPermissions.every(auth.can)" key="/procurement/landed-costs" @click="router.push('/procurement/landed-costs')">
              {{ locale === 'en' ? 'Procurement fees' : '采购费用' }}
            </a-menu-item>
            <a-menu-item v-if="group === '采购' && returnReadPermissions.every(auth.can)" key="/procurement/supplier-returns" @click="router.push('/procurement/supplier-returns')">
              {{ locale === 'en' ? 'Supplier returns' : '采购退供' }}
            </a-menu-item>
            <a-menu-item v-if="group === '系统' && canApprovalFlows"
              key="/settings/approval-flows" @click="router.push('/settings/approval-flows')">
              {{ t('nav.approvalFlows') }}</a-menu-item>
            <a-menu-item v-if="group === '系统' && canUserAdmin"
              key="/settings/users" @click="router.push('/settings/users')">
              {{ locale === 'en' ? 'Employee accounts' : '员工账号' }}
            </a-menu-item>
            <a-menu-item v-if="group === '系统' && canRolePermissions"
              key="/settings/roles" @click="router.push('/settings/roles')">
              {{ locale === 'en' ? 'Role permissions' : '角色权限' }}
            </a-menu-item>

            <a-menu-item v-if="group === '系统' && canDocumentNumbering"
              key="/settings/document-numbers" @click="router.push('/settings/document-numbers')">
              {{ locale === 'en' ? 'Document numbering' : '单据编号' }}
            </a-menu-item>

          </a-sub-menu>
        </template>
      </a-menu>
    </a-layout-sider>
    <a-layout>
      <a-layout-header class="app-header">
        <h1 v-if="pageTitle" class="app-header-title">{{ pageTitle }}</h1>
        <a-button v-if="canReminders" type="text" class="app-header-bell" data-testid="header-notifications"
          :aria-label="bellLabel"
          @click="openNotifications">
          <IconNotification :size="18" />
          <span v-if="unreadCount > 0" class="app-header-bell-badge" data-testid="header-notification-count">
            {{ unreadCount > 99 ? '99+' : unreadCount }}
          </span>
        </a-button>
        <a-dropdown :popup-visible="userMenuOpen" trigger="click" position="br"
          @popup-visible-change="onUserMenuVisible" @select="onUserSelect">
          <span class="app-user">
            <span class="app-user-name">{{ auth.user?.name_zh }}</span>
            <IconDown class="app-user-caret" />
          </span>
          <template #content>
            <a-doption value="change-password">{{ t('shell.changePassword') }}</a-doption>
            <a-doption value="logout">{{ t('shell.logout') }}</a-doption>
          </template>
        </a-dropdown>
        <LanguageSwitch />
      </a-layout-header>
      <a-layout-content ref="contentRef" class="app-content">
        <RouterView />
      </a-layout-content>
    </a-layout>
  </a-layout>
</template>
